import { useState, useEffect } from "react";
import { X, Pencil, User, Loader2, Upload, CreditCard } from "lucide-react";
import { toast } from "react-toastify";
import clientsService from "../../../../api/ClientsService";
import { ghanaRegions, ghanaLocationsByRegion } from "../../../../data/ghanaRegionsAndLocations";
import SearchableSelect from "../../../../components/SearchableSelect";

// Only Ghana Card is supported for now
const documentTypeOptions = [
  "Ghana Card",
];

const reverseDocumentTypeMapping = {
  "Ghana Card": "ghana_card",
};

const ClientEditModal = ({ client, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneDisplay: "",
    gender: "",
    dob: "",
    region: "",
    location: "",
    customLocation: "",
    idType: "",
    issueDate: "",
    expiryDate: "",
    idFrontImage: null,
    idBackImage: null,
    card_number: "",
  });
  const [originalImages, setOriginalImages] = useState({
    idCardFront: null,
    idCardBack: null,
  });

  useEffect(() => {
    if (!client) return;

    const parseDOB = (dobString) => {
      if (!dobString || dobString.length !== 8) return "";
      const day = dobString.slice(0, 2);
      const month = dobString.slice(2, 4);
      const year = dobString.slice(4, 8);
      return `${year}-${month}-${day}`;
    };

    const parseDateForInput = (dateString) => {
      if (!dateString) return "";
      try {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      } catch {
        return "";
      }
    };

    const formatPhoneForDisplay = (phone) => {
      if (!phone) return "";
      const p = String(phone).replace(/\D/g, "");
      if (p.startsWith("233")) return `0${p.slice(3)}`;
      return phone;
    };

    const rawDob = client.dob || "";
    const dobForForm =
      rawDob && rawDob.length === 8 ? parseDOB(rawDob) : parseDateForInput(rawDob);

    const clientRegion = client.region || "";
    const clientLocation = client.location || "";
    let locationValue = clientLocation;
    let customLocationValue = "";

    if (clientRegion && clientLocation) {
      const standardLocations = ghanaLocationsByRegion[clientRegion] || [];
      if (!standardLocations.includes(clientLocation)) {
        locationValue = "Other";
        customLocationValue = clientLocation;
      }
    }

    setFormData({
      firstName: client.firstName || "",
      lastName: client.lastName || "",
      email: client.email || "",
      phone: client.phone || "",
      phoneDisplay: formatPhoneForDisplay(client.phone || ""),
      gender: client.gender
        ? client.gender.charAt(0).toUpperCase() + client.gender.slice(1).toLowerCase()
        : "",
      dob: dobForForm,
      region: clientRegion,
      location: locationValue,
      customLocation: customLocationValue,
      idType: client.documentType || "",
      issueDate: parseDateForInput(client.issueDate || ""),
      expiryDate: parseDateForInput(client.expiryDate || ""),
      idFrontImage: client.idCardFront || null,
      idBackImage: client.idCardBack || null,
      card_number: client.cardNumber || "",
    });

    setOriginalImages({
      idCardFront: client.idCardFront || null,
      idCardBack: client.idCardBack || null,
    });
  }, [client]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneChange = (value) => {
    const digitsOnly = value.replace(/\D/g, "");
    let formatted = digitsOnly.startsWith("233")
      ? `0${digitsOnly.slice(3)}`
      : digitsOnly;
    if (formatted.length > 10) formatted = formatted.slice(0, 10);
    setFormData((prev) => ({
      ...prev,
      phoneDisplay: formatted,
      phone: formatted.startsWith("0")
        ? `233${formatted.slice(1)}`
        : formatted,
    }));
  };

  const isPhoneValid = () => {
    if (!formData.phoneDisplay) return false;
    if (formData.phoneDisplay.startsWith("233"))
      return formData.phoneDisplay.length === 12;
    if (formData.phoneDisplay.startsWith("0"))
      return formData.phoneDisplay.length === 10;
    return false;
  };

  const handleImageUpload = (field, file) => {
    const reader = new FileReader();
    reader.onload = (e) =>
      setFormData((prev) => ({ ...prev, [field]: e.target.result }));
    reader.readAsDataURL(file);
  };

  const removeImage = (field) => {
    setFormData((prev) => ({ ...prev, [field]: null }));
  };

  const isImageChanged = (current, original) => {
    if (!current) return false;
    if (!original) return current.startsWith("data:image");
    return current.startsWith("data:image") && current !== original;
  };

  const formatDOB = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  };

  const isFormValid = () => {
    const required = [
      "firstName",
      "lastName",
      "email",
      "phone",
      "gender",
      "dob",
      "region",
      "location",
    ];
    const filled = required.every((field) => {
      if (field === "location")
        return (
          formData.location ||
          (formData.location === "Other" && formData.customLocation)
        );
      return formData[field]?.trim();
    });
    return filled && isPhoneValid();
  };

  const createUpdatePayload = () => {
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      phone_number: formData.phone,
      location:
        formData.location === "Other"
          ? formData.customLocation
          : formData.location,
      dob: formatDOB(formData.dob),
      region: formData.region,
      gender: formData.gender.toLowerCase(),
    };

    if (formData.idType) {
      payload.document_type = reverseDocumentTypeMapping[formData.idType] || "";
      if (formData.card_number?.trim())
        payload.card_number = formData.card_number.trim();
      if (formData.issueDate) payload.issue_date = formData.issueDate;
      if (formData.expiryDate) payload.expiry_date = formData.expiryDate;
    }

    if (formData.idFrontImage) {
      payload.id_card_front = isImageChanged(
        formData.idFrontImage,
        originalImages.idCardFront
      )
        ? formData.idFrontImage
        : originalImages.idCardFront;
    }

    if (formData.idBackImage) {
      payload.id_card_back = isImageChanged(
        formData.idBackImage,
        originalImages.idCardBack
      )
        ? formData.idBackImage
        : originalImages.idCardBack;
    }

    return payload;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!client?.slug) {
      toast.error("Client identifier missing");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = createUpdatePayload();
      const response = await clientsService.updateClient(client.slug, payload);

      if (response.status) {
        toast.success(response.message || "Client updated successfully!");
        onSuccess();
      } else {
        toast.error(response.message || "Failed to update client");
        if (response.validationErrors)
          console.error("Validation errors:", response.validationErrors);
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Update error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!client) return null;

  return (
    <div className="flex overflow-y-auto fixed inset-0 z-50 justify-center items-center p-4">
      <div
        className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={isSubmitting ? undefined : onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden my-8">
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-blue-500 to-indigo-600">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="flex justify-center items-center w-12 h-12 bg-white bg-opacity-20 rounded-full border-2 border-white border-opacity-30 backdrop-blur-sm">
                <Pencil className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Edit Client</h3>
                <p className="text-blue-100 text-sm mt-0.5">
                  Update client information
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="p-2 text-white rounded-lg transition-all duration-200 hover:text-blue-100 hover:bg-white hover:bg-opacity-20 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
          <div className="space-y-6">
            {/* Personal Information */}
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <h4 className="flex items-center mb-5 text-lg font-bold text-gray-900">
                <User className="mr-2 w-5 h-5 text-blue-600" />
                Personal Information
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneDisplay}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="0XXXXXXXXX"
                    className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Gender *
                  </label>
                  <div className="flex gap-3">
                    {["Male", "Female"].map((option) => (
                      <label
                        key={option}
                        className={`flex items-center gap-2 px-4 py-3 flex-1 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.gender.toLowerCase() === option.toLowerCase()
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="gender"
                          value={option}
                          checked={
                            formData.gender.toLowerCase() === option.toLowerCase()
                          }
                          onChange={() => handleInputChange("gender", option)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span
                          className={`text-sm font-medium ${
                            formData.gender.toLowerCase() ===
                            option.toLowerCase()
                              ? "text-blue-700"
                              : "text-gray-700"
                          }`}
                        >
                          {option}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    value={formData.dob}
                    onChange={(e) => handleInputChange("dob", e.target.value)}
                    className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Region *
                  </label>
                  <SearchableSelect
                    options={ghanaRegions}
                    value={formData.region}
                    onChange={(value) => {
                      handleInputChange("region", value);
                      handleInputChange("location", "");
                      handleInputChange("customLocation", "");
                    }}
                    placeholder="Select Region"
                    label="region"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Location *
                  </label>
                  {formData.region ? (
                    <>
                      <SearchableSelect
                        options={ghanaLocationsByRegion[formData.region] || []}
                        value={formData.location}
                        onChange={(value) => {
                          if (value === "Other") {
                            handleInputChange("location", "Other");
                          } else {
                            handleInputChange("location", value);
                            handleInputChange("customLocation", "");
                          }
                        }}
                        placeholder="Select Location"
                        showOther={true}
                        otherPlaceholder="Enter custom location"
                        label="location"
                      />
                      {formData.location === "Other" && (
                        <input
                          type="text"
                          value={formData.customLocation}
                          onChange={(e) =>
                            handleInputChange("customLocation", e.target.value)
                          }
                          placeholder="Enter custom location"
                          className="px-4 py-3 mt-2 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      )}
                    </>
                  ) : (
                    <input
                      type="text"
                      value={formData.location}
                      placeholder="Select region first"
                      disabled
                      className="px-4 py-3 w-full bg-gray-100 rounded-lg border-2 border-gray-200 cursor-not-allowed"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* ID Document Information */}
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <h4 className="flex items-center mb-5 text-lg font-bold text-gray-900">
                <CreditCard className="mr-2 w-5 h-5 text-blue-600" />
                ID Document Information (Optional)
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Document Type <span className="text-xs text-gray-500">(Ghana Card only)</span>
                  </label>
                  <select
                    value={formData.idType}
                    onChange={(e) => handleInputChange("idType", e.target.value)}
                    className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Document Type</option>
                    {documentTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                {formData.idType && (
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Card Number
                    </label>
                    <input
                      type="text"
                      value={formData.card_number}
                      onChange={(e) =>
                        handleInputChange("card_number", e.target.value)
                      }
                      placeholder="Enter card number"
                      className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
                {formData.idType && (
                  <>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Issue Date
                      </label>
                      <input
                        type="date"
                        value={formData.issueDate}
                        onChange={(e) =>
                          handleInputChange("issueDate", e.target.value)
                        }
                        className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        value={formData.expiryDate}
                        onChange={(e) =>
                          handleInputChange("expiryDate", e.target.value)
                        }
                        className="px-4 py-3 w-full rounded-lg border-2 border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* ID Card Images */}
              {formData.idType && (
                <div className="mt-6">
                  <label className="flex gap-2 items-center mb-4 text-sm font-medium text-gray-700">
                    <CreditCard className="w-4 h-4" />
                    ID Card Images
                  </label>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <label className="block mb-3 text-sm font-medium text-gray-700">
                        Front
                      </label>
                      {formData.idFrontImage ? (
                        <div className="relative">
                          <img
                            src={formData.idFrontImage}
                            alt="ID card front"
                            className="object-cover w-full h-48 rounded-lg border-2 border-gray-200"
                          />
                          <button
                            onClick={() => removeImage("idFrontImage")}
                            className="flex absolute -top-2 -right-2 justify-center items-center w-8 h-8 text-white bg-red-500 rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <label className="absolute bottom-2 left-2 block px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg cursor-pointer hover:bg-blue-700">
                            <Upload className="inline mr-1 w-3 h-3" />
                            Change
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                e.target.files[0] &&
                                handleImageUpload("idFrontImage", e.target.files[0])
                              }
                              className="hidden"
                            />
                          </label>
                        </div>
                      ) : (
                        <label className="block w-full h-48 rounded-lg border-2 border-gray-300 border-dashed cursor-pointer hover:border-blue-400">
                          <div className="flex flex-col justify-center items-center h-full text-gray-600">
                            <Upload className="mb-2 w-8 h-8" />
                            <span className="text-sm">Upload front image</span>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                              e.target.files[0] &&
                              handleImageUpload("idFrontImage", e.target.files[0])
                            }
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                    <div>
                      <label className="block mb-3 text-sm font-medium text-gray-700">
                        Back
                      </label>
                      {formData.idBackImage ? (
                        <div className="relative">
                          <img
                            src={formData.idBackImage}
                            alt="ID card back"
                            className="object-cover w-full h-48 rounded-lg border-2 border-gray-200"
                          />
                          <button
                            onClick={() => removeImage("idBackImage")}
                            className="flex absolute -top-2 -right-2 justify-center items-center w-8 h-8 text-white bg-red-500 rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <label className="absolute bottom-2 left-2 block px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg cursor-pointer hover:bg-blue-700">
                            <Upload className="inline mr-1 w-3 h-3" />
                            Change
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                e.target.files[0] &&
                                handleImageUpload("idBackImage", e.target.files[0])
                              }
                              className="hidden"
                            />
                          </label>
                        </div>
                      ) : (
                        <label className="block w-full h-48 rounded-lg border-2 border-gray-300 border-dashed cursor-pointer hover:border-blue-400">
                          <div className="flex flex-col justify-center items-center h-full text-gray-600">
                            <Upload className="mb-2 w-8 h-8" />
                            <span className="text-sm">Upload back image</span>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                              e.target.files[0] &&
                              handleImageUpload("idBackImage", e.target.files[0])
                            }
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end px-6 py-4 bg-white border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white rounded-lg border-2 border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !isFormValid()}
            className="flex gap-2 items-center px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg transition-all hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update Client"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientEditModal;
